"use client";

// Root-layout error boundary. This only fires when something throws in
// `src/app/layout.tsx` itself, the global providers (PostHogProvider,
// auth provider), or anywhere else outside a more specific error.tsx
// boundary's reach. When active it REPLACES the entire <html>/<body> —
// so this file cannot use design-system components that depend on root
// layout context.
//
// Capture to PostHog with `boundary: "global"` so we can distinguish
// these (rare, severe) errors from segment-level ones.

import { useEffect } from "react";
import posthog from "posthog-js";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (error.message === "NEXT_REDIRECT" || error.message === "NEXT_NOT_FOUND") {
      return;
    }
    posthog.captureException(error, {
      boundary: "app/global-error.tsx",
      digest: error.digest,
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          margin: 0,
          padding: "4rem 1.5rem",
          background: "#fafaf7",
          color: "#1a1a1a",
        }}
      >
        <div
          style={{
            maxWidth: "28rem",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
            Something went wrong
          </h2>
          <p
            style={{
              marginTop: "0.75rem",
              fontSize: "0.875rem",
              color: "#666",
            }}
          >
            The app crashed before it could render. We&apos;ve logged the
            error.
          </p>
          {error.digest && (
            <p
              style={{
                marginTop: "0.5rem",
                fontSize: "0.6875rem",
                fontFamily: "ui-monospace, monospace",
                color: "#999",
              }}
            >
              ref: {error.digest}
            </p>
          )}
          <button
            onClick={reset}
            style={{
              marginTop: "1.5rem",
              padding: "0.5rem 1rem",
              border: "1px solid #ccc",
              borderRadius: "0.5rem",
              background: "white",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
